import type { ReportResponsePayload, ReportRow } from '../models/report.types'

import type { MultiMetricPeriod } from './aggregateMultiMetricSeries'
import type { MultiMetricFrequency } from './buildMultiMetricChartOptions'
import { dayKey } from './ga4Override'

/**
 * The rows the overview chart draws. A compare request answers both periods in `data` itself, each
 * row carrying its baseline as `prev_<metric>` (ORP-24984); the frequency-keyed `previous_<freq>`
 * bag is no longer appended — v3 `OrphexDashboardChartArea.vue` dropped it with the same fix.
 */
export function multiMetricResponseRows(
  response: ReportResponsePayload | null | undefined,
): ReportRow[] {
  return Array.isArray(response?.data) ? response.data : []
}

export interface MultiMetricDateRange {
  start: string
  end: string
}

/** The main range (`date_start`/`date_end`) of a report's post params, as day keys. */
export function multiMetricDateRange(postData?: string | null): MultiMetricDateRange | null {
  const params = new URLSearchParams(postData ?? '')
  const start = dayKey(params.get('date_start'))
  const end = dayKey(params.get('date_end'))
  return start && end ? { start, end } : null
}

function shiftDays(key: string, days: number): string {
  const ms = Date.parse(`${key}T00:00:00Z`)
  return Number.isFinite(ms) ? new Date(ms + days * 86_400_000).toISOString().slice(0, 10) : key
}

/**
 * Which period a row's chart key falls in. The range start is widened to the bucket a weekly,
 * monthly or yearly key names, so the bucket the range opens in still counts as current. Without a
 * range the row's own `is_previous` decides, as before.
 */
export function multiMetricPeriodOf(
  row: Readonly<ReportRow>,
  dateKey: string,
  range: MultiMetricDateRange | null,
  frequency: MultiMetricFrequency,
): MultiMetricPeriod {
  if (!range || !dateKey) return row.is_previous ? 'before' : 'current'
  const start =
    frequency === 'year'
      ? `${range.start.slice(0, 4)}-01-01`
      : frequency === 'month_of_year'
        ? `${range.start.slice(0, 7)}-01`
        : frequency === 'week'
          ? shiftDays(range.start, -6)
          : range.start
  const key = dateKey.slice(0, 10)
  return key >= start && key <= range.end ? 'current' : 'before'
}

/** A comparison row is read from its `prev_<metric>` baseline when it carries one. */
export function multiMetricValueField(
  row: Readonly<ReportRow>,
  metric: string,
  period: MultiMetricPeriod,
): string {
  const prev = `prev_${metric}`
  return period === 'before' && prev in row ? prev : metric
}

/** Stable ISO-like chart key for every frequency the legacy overview selector offered. */
export function multiMetricDateKey(
  row: Readonly<ReportRow>,
  frequency: MultiMetricFrequency,
): string {
  if (frequency === 'year') {
    const year = String(row.year ?? row.year_of_date ?? row.yearly ?? '')
    return year ? `${year}-01-01` : ''
  }
  if (frequency === 'month_of_year') {
    const raw = String(row.month_of_year ?? row.month ?? '')
    const year = String(row.year ?? '')
    const parsed = new Date(`${raw} 1, ${year}`)
    if (Number.isNaN(parsed.getTime())) return raw
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-01`
  }
  return String(row[frequency] ?? row.date ?? '')
}
